import React from "react";
import "./SkeletonLoaders.css";

/**
 * Single Product Card Skeleton
 * Exactly mirrors .pic2-card geometry and dark luxury styling
 */
export const ProductCardSkeleton = ({ style = {} }) => {
  return (
    <div className="shz-card-skeleton" style={style}>
      {/* Image Wrap */}
      <div className="shz-card-skeleton-image-wrap shz-skeleton-shimmer">
        <div className="shz-card-skeleton-heart-pill" />
        <div className="shz-card-skeleton-badge-pill" />
      </div>

      {/* Body */}
      <div className="shz-card-skeleton-body">
        {/* Rating row: 5 small stars + review count */}
        <div className="shz-card-skeleton-rating-row">
          <div className="shz-card-skeleton-stars">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="shz-card-skeleton-star shz-skeleton-shimmer"
              />
            ))}
          </div>
          <div className="shz-card-skeleton-reviews shz-skeleton-shimmer" />
        </div>

        {/* Title: 2 lines */}
        <div className="shz-card-skeleton-title-line-1 shz-skeleton-shimmer" />
        <div className="shz-card-skeleton-title-line-2 shz-skeleton-shimmer" />

        {/* Category tag */}
        <div className="shz-card-skeleton-category shz-skeleton-shimmer" />

        {/* Price row */}
        <div className="shz-card-skeleton-price-row">
          <div className="shz-card-skeleton-orig-price shz-skeleton-shimmer" />
          <div className="shz-card-skeleton-sale-price shz-skeleton-shimmer" />
        </div>
      </div>
    </div>
  );
};

/**
 * Responsive Grid of Product Card Skeletons (Shop & Category Page)
 */
export const ProductGridSkeleton = ({ count = 8, style = {} }) => {
  const items = Array.from({ length: count });
  return (
    <div className="shz-product-grid-skeleton" style={style}>
      {items.map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
};

/**
 * Horizontal Row of Product Card Skeletons (Home Page Slick Slider placeholder)
 */
export const ProductCardSkeletonRow = ({ count = 4, style = {} }) => {
  const items = Array.from({ length: count });
  return (
    <div className="shz-slider-skeleton-row" style={style}>
      {items.map((_, i) => (
        <div key={i} className="shz-slider-skeleton-item">
          <ProductCardSkeleton />
        </div>
      ))}
    </div>
  );
};

/**
 * Product Detail Page Skeleton (left-sidebar / productDetailsI)
 */
export const ProductDetailSkeleton = () => {
  return (
    <div className="shz-detail-skeleton-wrap">
      <div className="shz-detail-skeleton-grid">
        {/* Left Column: Gallery */}
        <div className="shz-detail-skeleton-gallery">
          <div className="shz-detail-skeleton-main-img shz-skeleton-shimmer" />
          <div className="shz-detail-skeleton-thumbs">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="shz-detail-skeleton-thumb shz-skeleton-shimmer"
              />
            ))}
          </div>
        </div>

        {/* Right Column: Product Info & Actions */}
        <div className="shz-detail-skeleton-info">
          {/* Category Tag */}
          <div className="shz-detail-skeleton-tag shz-skeleton-shimmer" />

          {/* Title */}
          <div className="shz-detail-skeleton-title-1 shz-skeleton-shimmer" />
          <div className="shz-detail-skeleton-title-2 shz-skeleton-shimmer" />

          {/* Ratings row */}
          <div className="shz-detail-skeleton-ratings">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{ width: 16, height: 16, borderRadius: 2 }}
                className="shz-skeleton-shimmer"
              />
            ))}
            <div
              style={{ width: 90, height: 14, borderRadius: 4, marginLeft: 10 }}
              className="shz-skeleton-shimmer"
            />
          </div>

          {/* Price */}
          <div className="shz-detail-skeleton-price-box">
            <div
              style={{ width: 120, height: 28, borderRadius: 6 }}
              className="shz-skeleton-shimmer"
            />
            <div
              style={{ width: 80, height: 20, borderRadius: 4 }}
              className="shz-skeleton-shimmer"
            />
          </div>

          {/* Short description */}
          <div
            className="shz-detail-skeleton-desc-line shz-skeleton-shimmer"
            style={{ width: "95%" }}
          />
          <div
            className="shz-detail-skeleton-desc-line shz-skeleton-shimmer"
            style={{ width: "85%" }}
          />
          <div
            className="shz-detail-skeleton-desc-line shz-skeleton-shimmer"
            style={{ width: "70%" }}
          />

          {/* Variant pills */}
          <div style={{ marginTop: 8 }}>
            <div
              style={{ width: 60, height: 14, borderRadius: 3, marginBottom: 8 }}
              className="shz-skeleton-shimmer"
            />
            <div className="shz-detail-skeleton-variants-row">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="shz-detail-skeleton-variant-pill shz-skeleton-shimmer"
                />
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="shz-detail-skeleton-btn-row">
            <div className="shz-detail-skeleton-btn-primary shz-skeleton-shimmer" />
            <div className="shz-detail-skeleton-btn-secondary shz-skeleton-shimmer" />
          </div>

          {/* Trust badges */}
          <div className="shz-detail-skeleton-perks">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="shz-detail-skeleton-perk shz-skeleton-shimmer"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Category Circles Skeleton (Home page "Discover Your Collection")
 */
export const CategoryCirclesSkeleton = ({ count = 6 }) => {
  const items = Array.from({ length: count });
  return (
    <div className="shz-category-skeleton-row">
      {items.map((_, i) => (
        <div key={i} className="shz-category-skeleton-item">
          <div className="shz-category-skeleton-circle shz-skeleton-shimmer" />
          <div className="shz-category-skeleton-title shz-skeleton-shimmer" />
        </div>
      ))}
    </div>
  );
};

/**
 * Live Search Suggestion Skeleton (Dropdown in search bar)
 */
export const SearchSuggestionSkeleton = ({ count = 3 }) => {
  const items = Array.from({ length: count });
  return (
    <div>
      {items.map((_, i) => (
        <div key={i} className="shz-search-suggestion-skeleton">
          <div className="shz-search-suggestion-thumb shz-skeleton-shimmer" />
          <div className="shz-search-suggestion-meta">
            <div
              style={{ width: "80%", height: 16, borderRadius: 4 }}
              className="shz-skeleton-shimmer"
            />
            <div
              style={{ width: "40%", height: 12, borderRadius: 3 }}
              className="shz-skeleton-shimmer"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Banner Shimmer Skeleton (Hero or promo banner placeholder)
 */
export const BannerSkeleton = ({ height = 320, style = {} }) => {
  return (
    <div
      className="shz-banner-skeleton shz-skeleton-shimmer"
      style={{ height, ...style }}
    />
  );
};

export default {
  ProductCardSkeleton,
  ProductGridSkeleton,
  ProductCardSkeletonRow,
  ProductDetailSkeleton,
  CategoryCirclesSkeleton,
  SearchSuggestionSkeleton,
  BannerSkeleton,
};
